import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaBrain, FaChrome, FaChartLine } from 'react-icons/fa';
import './FYP.css';

const FYP = () => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollPosition = 0;
    const scrollSpeed = 0.5;

    const scroll = () => {
      scrollPosition += scrollSpeed;
      if (scrollPosition >= scrollContainer.scrollWidth / 2) {
        scrollPosition = 0;
      }
      scrollContainer.scrollLeft = scrollPosition;
    };

    const intervalId = setInterval(scroll, 20);
    return () => clearInterval(intervalId);
  }, []);

  const images = Array.from({ length: 8 }, (_, i) => `/fyp/${i + 1}.png`);
  const duplicatedImages = [...images, ...images];

  const features = [
    {
      icon: FaChrome,
      title: 'Browser Integration',
      description: 'Real-time network traffic capture directly from Chrome browser'
    },
    {
      icon: FaBrain,
      title: 'AI-Powered Detection',
      description: 'Deep Neural Networks and XGBoost for intelligent threat classification'
    },
    {
      icon: FaChartLine,
      title: 'Live Dashboard',
      description: 'Dynamic visualization of CPU, memory, network usage, and threat trends'
    },
    {
      icon: FaShieldAlt,
      title: 'Flow-Based Analysis',
      description: 'Advanced feature extraction and attack detection using OD-IDS 2022 dataset'
    }
  ];

  return (
    <section id="fyp" className="fyp">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <h2 className="section-title">Final Year Project</h2>
          <div className="title-underline"></div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="fyp-content"
        >
          <div className="fyp-header">
            <h3 className="fyp-title">
              <FaShieldAlt className="fyp-icon" />
              MalStrike
            </h3>
            <p className="fyp-subtitle">AI-Powered Intrusion Detection Chrome Extension</p>
          </div>

          <div className="fyp-description">
            <p>
              MalStrike is a real-time AI-powered Intrusion Detection Chrome Extension developed as my Final Year Project. 
              It captures live browser network traffic, analyzes flow-based features, and detects malicious activity using 
              machine learning models including Deep Neural Networks and XGBoost.
            </p>
            <p>
              The system integrates a dynamic dashboard that visualizes CPU, memory, network usage, and threat trends in 
              real time. By combining browser-level monitoring with AI-based attack classification trained on the OD-IDS 2022 
              dataset, MalStrike provides intelligent threat detection directly within the browser environment.
            </p>
            <p>
              This project strengthened my skills in cybersecurity, machine learning, data preprocessing, model deployment, 
              and full-stack extension development.
            </p>
          </div>

          <div className="fyp-features">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <feature.icon className="feature-icon" />
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="fyp-gallery">
            <div className="gallery-scroll" ref={scrollRef}>
              <div className="gallery-track">
                {duplicatedImages.map((img, index) => (
                  <div key={index} className="gallery-item">
                    <img src={img} alt={`MalStrike Screenshot ${(index % 8) + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FYP;
